/* LMC Orlando — Compare (Chunk 9).

   Three custom elements + a shared state module:
   - LMCCompareStore (module-level)         — sessionStorage-backed list
   - <lmc-compare-trigger>                  — PLP card "+ Compare" button
   - <lmc-compare-bar>                      — site-wide sticky bottom bar
   - <lmc-compare-table>                    — /pages/compare hydrator

   State shape: Array of denormalized items
     [{ handle, title, url, image }]
   Capped at 4. Persists in sessionStorage under key 'lmc-compare'.
   Every mutation fires `lmc:compare-changed` on document. */

(function () {
  'use strict';

  const STORAGE_KEY = 'lmc-compare';
  const MAX_ITEMS = 4;
  const productCache = new Map();

  /* ───────────── Store ───────────── */
  const LMCCompareStore = {
    get() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (err) {
        return [];
      }
    },

    has(handle) {
      return this.get().some((item) => item.handle === handle);
    },

    add(item) {
      const list = this.get();
      if (list.some((existing) => existing.handle === item.handle)) return { ok: true, reason: 'exists' };
      if (list.length >= MAX_ITEMS) {
        document.dispatchEvent(new CustomEvent('lmc:compare-full', { detail: { max: MAX_ITEMS } }));
        return { ok: false, reason: 'full' };
      }
      list.push(item);
      this.save(list);
      return { ok: true };
    },

    remove(handle) {
      const list = this.get().filter((item) => item.handle !== handle);
      this.save(list);
    },

    clear() {
      this.save([]);
    },

    save(list) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (err) {
        // sessionStorage write failed (private mode / quota); silently degrade
      }
      document.dispatchEvent(
        new CustomEvent('lmc:compare-changed', { detail: { list, count: list.length } })
      );
    },
  };

  // Expose for debugging / future extensions.
  window.LMCCompareStore = LMCCompareStore;

  /* ───────────── <lmc-compare-trigger> ─────────────
     Wraps the "+ Compare" button on PLP cards. Reads product handle +
     denormalized data from data attributes; doesn't need a fetch. */
  class LMCCompareTrigger extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onStoreChange = this.refreshLabel.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.handle = this.dataset.compareProductHandle;
      this.title = this.dataset.compareProductTitle;
      this.url = this.dataset.compareProductUrl;
      this.image = this.dataset.compareProductImage || '';
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('lmc:compare-changed', this.onStoreChange);
      this.refreshLabel();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('lmc:compare-changed', this.onStoreChange);
      clearTimeout(this.fullTimer);
    }

    onClick(event) {
      event.preventDefault();
      if (!this.handle) return;
      if (LMCCompareStore.has(this.handle)) {
        LMCCompareStore.remove(this.handle);
      } else {
        const result = LMCCompareStore.add({
          handle: this.handle,
          title: this.title,
          url: this.url,
          image: this.image,
        });
        if (!result.ok && result.reason === 'full') this.flashFull();
      }
    }

    // At-cap feedback: the store rejects the add with no state change (so no
    // lmc:compare-changed fires): flash the reason on the button itself.
    flashFull() {
      if (!this.button) return;
      const labelEl = this.button.querySelector('[data-compare-label]') || this.button;
      labelEl.textContent = 'Compare full (' + MAX_ITEMS + ' max)';
      clearTimeout(this.fullTimer);
      this.fullTimer = setTimeout(() => this.refreshLabel(), 2000);
    }

    refreshLabel() {
      if (!this.button) return;
      const isIn = LMCCompareStore.has(this.handle);
      this.button.classList.toggle('is-active', isIn);
      this.button.setAttribute('aria-pressed', isIn ? 'true' : 'false');
      const labelEl = this.button.querySelector('[data-compare-label]') || this.button;
      labelEl.textContent = isIn ? '✓ In compare' : '+ Compare';
    }
  }

  if (!customElements.get('lmc-compare-trigger')) {
    customElements.define('lmc-compare-trigger', LMCCompareTrigger);
  }

  /* ───────────── <lmc-compare-bar> ─────────────
     Persistent site-wide sticky bar. Hidden until items exist. */
  class LMCCompareBar extends HTMLElement {
    constructor() {
      super();
      this.onStoreChange = this.render.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onFull = this.showFullNotice.bind(this);
    }

    connectedCallback() {
      this.slotsEl = this.querySelector('[data-bar-slots]');
      this.countEl = this.querySelector('[data-bar-count]');
      this.compareLinkEl = this.querySelector('[data-bar-compare]');
      this.clearEl = this.querySelector('[data-bar-clear]');
      this.noticeEl = this.querySelector('[data-bar-notice]');
      document.addEventListener('lmc:compare-changed', this.onStoreChange);
      document.addEventListener('lmc:compare-full', this.onFull);
      this.addEventListener('click', this.onClick);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('lmc:compare-changed', this.onStoreChange);
      document.removeEventListener('lmc:compare-full', this.onFull);
      this.removeEventListener('click', this.onClick);
      clearTimeout(this.noticeTimer);
    }

    // The store dispatches lmc:compare-full when an add is rejected at the
    // cap; surface it as a transient toast above the bar (role="status" in
    // the snippet makes it a polite live region for screen readers).
    showFullNotice(event) {
      if (!this.noticeEl) return;
      const max = (event.detail && event.detail.max) || MAX_ITEMS;
      this.noticeEl.textContent = 'Compare is full: remove a chair to add another (max ' + max + ').';
      this.noticeEl.hidden = false;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        this.noticeEl.hidden = true;
      }, 3500);
    }

    onClick(event) {
      const removeBtn = event.target.closest('[data-bar-remove]');
      if (removeBtn) {
        event.preventDefault();
        LMCCompareStore.remove(removeBtn.dataset.barRemove);
        return;
      }
      if (event.target.closest('[data-bar-clear]')) {
        event.preventDefault();
        LMCCompareStore.clear();
        return;
      }
      // Block activation of the Compare CTA when fewer than 2 chairs are
      // staged (visual is-disabled is not enough — keyboard users can still
      // hit Enter on the anchor).
      if (this.compareLinkEl && event.target.closest('[data-bar-compare]')) {
        if (this.compareLinkEl.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        }
      }
    }

    render() {
      const list = LMCCompareStore.get();
      const empty = list.length === 0;
      this.hidden = empty;
      if (empty) return;
      if (this.countEl) this.countEl.textContent = list.length;
      if (this.compareLinkEl) {
        const disabled = list.length < 2;
        this.compareLinkEl.classList.toggle('is-disabled', disabled);
        if (disabled) {
          this.compareLinkEl.setAttribute('aria-disabled', 'true');
          this.compareLinkEl.setAttribute('tabindex', '-1');
          // Preserve original href to restore later
          if (!this.compareLinkHref) this.compareLinkHref = this.compareLinkEl.getAttribute('href');
          this.compareLinkEl.removeAttribute('href');
        } else {
          this.compareLinkEl.removeAttribute('aria-disabled');
          this.compareLinkEl.removeAttribute('tabindex');
          if (this.compareLinkHref) this.compareLinkEl.setAttribute('href', this.compareLinkHref);
        }
      }
      if (!this.slotsEl) return;
      this.slotsEl.innerHTML = list
        .map(
          (item) => `
            <div class="lmc-compare-bar__slot" title="${escapeHtml(item.title)}">
              ${item.image ? `<img src="${escapeHtml(item.image)}" alt="">` : ''}
              <button type="button" class="lmc-compare-bar__remove" data-bar-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)} from compare">×</button>
            </div>
          `
        )
        .join('');
    }
  }

  if (!customElements.get('lmc-compare-bar')) {
    customElements.define('lmc-compare-bar', LMCCompareBar);
  }

  /* ───────────── <lmc-compare-table> ─────────────
     On /pages/compare. Reads state, fetches each product's native data
     via /products/{handle}.js, populates the product-column header cells. */
  class LMCCompareTable extends HTMLElement {
    constructor() {
      super();
      this.onStoreChange = this.refresh.bind(this);
      this.onClick = this.onClick.bind(this);
      // Monotonic across reconnects so an in-flight refresh from a prior
      // mount can't pass the version guard after the element re-attaches.
      this.refreshId = 0;
    }

    connectedCallback() {
      this.colsRoot = this.querySelector('[data-compare-cols]');
      this.cellsRoots = Array.from(this.querySelectorAll('[data-compare-row-values]'));
      this.emptyEl = document.querySelector('[data-compare-empty]');
      this.tableEl = this; // hide whole table when empty
      document.addEventListener('lmc:compare-changed', this.onStoreChange);
      this.addEventListener('click', this.onClick);
      this.refresh();
    }

    disconnectedCallback() {
      // Invalidate any pending refresh — the post-await guard will see a
      // newer id and bail out before mutating DOM that's no longer attached.
      this.refreshId += 1;
      document.removeEventListener('lmc:compare-changed', this.onStoreChange);
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const removeBtn = event.target.closest('[data-col-remove]');
      if (removeBtn) {
        event.preventDefault();
        LMCCompareStore.remove(removeBtn.dataset.colRemove);
        return;
      }
      const addBtn = event.target.closest('[data-compare-add]');
      if (addBtn) {
        event.preventDefault();
        addToCart(addBtn);
      }
    }

    async refresh() {
      // Version this refresh so a slow in-flight one (e.g., user added a
      // chair, then immediately removed one) can't overwrite the newer
      // render with stale data. refreshId is monotonic — initialized in
      // the constructor and bumped on disconnect, so cross-mount races
      // are caught too.
      this.refreshId += 1;
      const myId = this.refreshId;

      const list = LMCCompareStore.get();
      if (list.length === 0) {
        if (this.emptyEl) this.emptyEl.hidden = false;
        this.hidden = true;
        return;
      }
      if (this.emptyEl) this.emptyEl.hidden = true;
      this.hidden = false;

      this.style.setProperty('--lmc-compare-cols', list.length);

      // Fetch all products in parallel (cached per handle)
      const products = await Promise.all(list.map((item) => fetchProduct(item.handle)));

      if (myId !== this.refreshId) return; // a newer refresh started while we awaited
      this.renderProductCols(list, products);
      this.renderSpecRows(list, products);
    }

    renderProductCols(list, products) {
      if (!this.colsRoot) return;
      this.colsRoot.innerHTML = list
        .map((item, i) => {
          const product = products[i];
          if (!product) {
            return `
              <div class="lmc-compare__prod lmc-compare__prod--missing">
                <button type="button" class="lmc-compare__remove" data-col-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)}">×</button>
                <p>Product unavailable</p>
                <p class="lmc-compare__prod-handle">${escapeHtml(item.handle)}</p>
              </div>
            `;
          }
          const compareAt = product.compare_at_price && product.compare_at_price > product.price ? product.compare_at_price : null;
          const price = formatMoney(product.price);
          const strike = compareAt ? formatMoney(compareAt) : '';
          const image = (product.featured_image || (product.images && product.images[0]) || '');
          // Affirm placeholder — price / 48 (matches the product card's floor math).
          const monthly = formatMoney(Math.floor(product.price / 4800) * 100);
          // First available variant for the Add to Cart button.
          const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
          const variantId = variant ? variant.id : '';
          const soldOut = !variant || variant.available === false;
          return `
            <div class="lmc-compare__prod">
              <button type="button" class="lmc-compare__remove" data-col-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)} from compare">×</button>
              <a href="${escapeHtml(product.url)}" class="lmc-compare__prod-img">
                ${image ? `<img src="${escapeHtml(sizedImage(image, 600))}" alt="${escapeHtml(product.title)}">` : ''}
              </a>
              <p class="lmc-compare__prod-brand">${escapeHtml(product.vendor || '')}</p>
              <a href="${escapeHtml(product.url)}" class="lmc-compare__prod-name">${escapeHtml(product.title)}</a>
              <p class="lmc-compare__prod-price">
                ${strike ? `<span class="lmc-compare__prod-strike">${strike}</span>` : ''}
                <strong>${price}</strong>
              </p>
              <p class="lmc-compare__prod-sub">As low as ${monthly}/mo with Affirm</p>
              <div class="lmc-compare__prod-ctas">
                <a href="${escapeHtml(product.url)}" class="lmc-pill lmc-pill--outline">View Details</a>
                <button type="button" class="lmc-pill lmc-pill--dark" data-compare-add="${escapeHtml(String(variantId))}"${soldOut ? ' disabled' : ''}>${soldOut ? 'Sold out' : 'Add to Cart →'}</button>
              </div>
            </div>
          `;
        })
        .join('');
    }

    renderSpecRows(list, products) {
      this.cellsRoots.forEach((cellsRoot) => {
        const source = cellsRoot.dataset.specSource || 'metafield';
        const field = cellsRoot.dataset.specField || '';
        const cells = products.map((product, i) => {
          let value = '—';
          if (product) {
            value = resolveSpecValue(product, source, field) ?? '—';
          }
          return `<div class="lmc-compare__v" data-chair="${escapeHtml(list[i].title)}">${escapeHtml(value)}</div>`;
        });
        cellsRoot.innerHTML = cells.join('');
      });
    }
  }

  if (!customElements.get('lmc-compare-table')) {
    customElements.define('lmc-compare-table', LMCCompareTable);
  }

  /* ───────────── Decision bar (print + count) ───────────── */
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-compare-print]')) {
      event.preventDefault();
      window.print();
    }
  });

  function syncLiveCount(count) {
    document.querySelectorAll('[data-compare-live-count]').forEach((el) => {
      el.textContent = count;
    });
    const noun = count === 1 ? 'chair' : 'chairs';
    document.querySelectorAll('[data-compare-live-noun]').forEach((el) => {
      el.textContent = noun;
    });
  }
  document.addEventListener('lmc:compare-changed', (event) => {
    syncLiveCount(event.detail.count);
  });
  // Seed on first load — if sessionStorage already has items (e.g., user
  // navigated to /pages/compare directly after staging chairs elsewhere),
  // the badges otherwise stay at 0 until the next mutation.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncLiveCount(LMCCompareStore.get().length));
  } else {
    syncLiveCount(LMCCompareStore.get().length);
  }

  /* ───────────── Helpers ───────────── */
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const encoded = encodeURIComponent(handle);
      // Native /products.js for core fields (title, price, images, …) plus
      // the compare-data view for metafields, which /products.js omits.
      const [coreRes, metaRes] = await Promise.all([
        fetch('/products/' + encoded + '.js'),
        fetch('/products/' + encoded + '?view=compare-data'),
      ]);
      if (!coreRes.ok) return null;
      const product = await coreRes.json();
      product.metafields = {};
      if (metaRes.ok) {
        try {
          product.metafields = (await metaRes.json()) || {};
        } catch (err) {
          // Malformed/empty metafield payload — degrade to "—" cells.
        }
      }
      productCache.set(handle, product);
      return product;
    } catch (err) {
      return null;
    }
  }

  function resolveSpecValue(product, source, field) {
    if (!field) return null;
    if (source === 'product_field') {
      // Top-level product field (vendor, type, etc.)
      return product[field] != null ? String(product[field]) : null;
    }
    if (source === 'metafield') {
      // `field` is the full "namespace.key" (e.g. "custom.roller_system").
      // Values come from the compare-data view merged in fetchProduct;
      // unset metafields are absent → "—".
      const metafields = product.metafields || {};
      const value = metafields[field];
      return value != null && value !== '' ? String(value) : null;
    }
    if (source === 'option') {
      // /products/{handle}.js exposes `options` as an array of strings (the
      // option names — no values), so derive values by mapping variants to
      // option1 / option2 / option3 by index. Collect unique non-empty values.
      const optionNames = (product.options || []).map((o) => (o.name || o).toString());
      const optIdx = optionNames.findIndex((name) => name.toLowerCase() === field.toLowerCase());
      if (optIdx < 0) return null;
      const variantKey = 'option' + (optIdx + 1);
      const variants = product.variants || [];
      const values = [];
      const seen = new Set();
      for (const v of variants) {
        const value = v[variantKey];
        if (value && !seen.has(value)) {
          seen.add(value);
          values.push(value);
        }
      }
      if (values.length === 0) return null;
      return values.length === 1 ? values[0] : values.join(' / ');
    }
    return null;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src.replace(/(\.(?:jpe?g|png|webp|gif))(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  // Add the chair's first available variant to the cart, then pop the
  // store's cart notification — mirrors Dawn's product-form add flow
  // (cart_type is "notification"). Falls back to the /cart page if the
  // notification element isn't present.
  function addToCart(btn) {
    const variantId = btn.getAttribute('data-compare-add');
    if (!variantId || btn.dataset.adding === '1') return;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = { id: Number(variantId), quantity: 1 };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const original = btn.textContent;
    btn.dataset.adding = '1';
    btn.textContent = 'Adding…';
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((parsed) => {
        if (parsed && parsed.status) {
          // Restore the button so the shopper can retry (don't leave it
          // permanently disabled via the dataset.adding guard).
          btn.textContent = 'Unavailable';
          setTimeout(() => {
            btn.textContent = original;
            delete btn.dataset.adding;
          }, 2000);
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
          cart.renderContents(parsed);
        } else {
          window.location.href = window.routes ? window.routes.cart_url : '/cart';
          return;
        }
        btn.textContent = 'Added ✓';
        setTimeout(() => {
          btn.textContent = original;
          delete btn.dataset.adding;
        }, 1800);
      })
      .catch(() => {
        btn.textContent = original;
        delete btn.dataset.adding;
      });
  }

  // Format using the store's money_format (injected on <lmc-compare-table>)
  // so prices match Liquid's `| money` everywhere else ("$2,999.00" vs the
  // old comma-less "$2999"). Mirrors Shopify's canonical formatMoney.
  let MONEY_FORMAT = null;
  function moneyFormat() {
    if (MONEY_FORMAT == null) {
      const el = document.querySelector('lmc-compare-table');
      MONEY_FORMAT = (el && el.getAttribute('data-money-format')) || '${{amount}}';
    }
    return MONEY_FORMAT;
  }
  function formatMoney(cents) {
    if (cents == null) return '';
    const format = moneyFormat();
    const withDelimiters = (number, precision, thousands, decimal) => {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
})();
